import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getAllPatients, getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { ROUTES } from '../constants/routes';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { applyPatientToGenerationResult } from '../utils/patientContext';
import { continueHandoverPatientSelection } from '../utils/multiPatientGeneration';
import { translateWard } from '../i18n/translations';

function PatientOption({ patient, subtitle, onSelect, language, t, disabled = false }) {
  const displayName = getPatientDisplayName(patient, language);
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <button
      type="button"
      onClick={() => onSelect(patient)}
      disabled={disabled}
      className="card-surface flex w-full items-center gap-3 p-3 text-left transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-primary-mid bg-primary-light/50 text-sm font-bold text-primary">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-bold text-gray-900" dir="auto">
          {displayName}
        </p>
        <p className="text-xs text-gray-500">
          {t('room')} {patient.room} · {translateWard(t, patient.ward)}
        </p>
        {subtitle && <p className="mt-1 text-xs text-primary">{subtitle}</p>}
      </div>
    </button>
  );
}

function MatchedPatientChip({ name, room, t }) {
  return (
    <div className="rounded-xl border border-success/30 bg-success-light px-3 py-2 text-sm text-success-dark">
      <span className="font-semibold">{name}</span>
      {room ? <span className="text-success-dark/80"> · {t('room')} {room}</span> : null}
    </div>
  );
}

export default function PatientSelectScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { admittedPatients, setDraftGeneration } = useSession();
  const { t, language } = useLanguage();
  const [continuing, setContinuing] = useState(false);

  const generationResult = location.state?.generationResult;
  const patientMatch = location.state?.patientMatch;
  const patientHints = location.state?.patientHints;
  const multiPatientContext = location.state?.multiPatientContext;
  const recordingDurationSeconds = location.state?.recordingDurationSeconds || generationResult?.recordingDurationSeconds || null;

  const allPatients = useMemo(
    () => getAllPatients(admittedPatients),
    [admittedPatients]
  );

  const candidatePatients = useMemo(() => {
    const ids = new Set((patientMatch?.candidates || []).map((entry) => entry.patient.id));
    if (!ids.size) return allPatients;
    return allPatients.filter((patient) => ids.has(patient.id));
  }, [allPatients, patientMatch]);

  const reviewQueue = location.state?.reviewQueue || [];
  const reviewQueueIndex = location.state?.reviewQueueIndex ?? reviewQueue.length;

  const matchedPatients = useMemo(() => {
    const fromContext = (multiPatientContext?.resolvedPatients || []).map((entry) => ({
      id: entry.id,
      name: entry.name,
      room: entry.room,
    }));

    const fromQueue = reviewQueue
      .map((entry) => {
        if (!entry?.patientId) return null;
        const patient = getPatientById(entry.patientId, admittedPatients);
        if (!patient) return null;
        return {
          id: patient.id,
          name: getPatientDisplayName(patient, language),
          room: patient.room,
        };
      })
      .filter(Boolean);

    const seen = new Set();
    return [...fromContext, ...fromQueue].filter((entry) => {
      if (!entry?.id || seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
  }, [multiPatientContext, reviewQueue, admittedPatients, language]);

  const candidateSubtitle = (patientId) => {
    const entry = patientMatch?.candidates?.find((candidate) => candidate.patient.id === patientId);
    if (!entry) return null;
    if (entry.matchType === 'room_and_name') return t('patientMatchRoomAndName');
    if (entry.matchType === 'room') return t('patientMatchRoom');
    if (entry.matchType === 'name') return t('patientMatchName');
    return t('patientMatchFuzzy');
  };

  const goToReview = (queue, index = 0) => {
    const current = queue[index] || queue[0];
    if (!current) return;
    setDraftGeneration(current);
    navigate(ROUTES.REVIEW, {
      state: {
        ...current,
        reviewQueue: queue.length > 1 ? queue : null,
        reviewQueueIndex: index,
        recordingDurationSeconds,
      },
      replace: true,
    });
  };

  const handleSelect = async (patient) => {
    if (!generationResult || continuing) {
      if (!generationResult) navigate(ROUTES.PATIENTS, { replace: true });
      return;
    }

    setContinuing(true);

    try {
      const result = applyPatientToGenerationResult(generationResult, patient);
      const nextQueue = [
        ...reviewQueue.slice(0, reviewQueueIndex),
        result,
        ...reviewQueue.slice(reviewQueueIndex + 1),
      ];

      if (multiPatientContext?.mode === 'handover') {
        const handoverOutcome = continueHandoverPatientSelection({
          parentResult: multiPatientContext.parentResult,
          handoverResolutions: multiPatientContext.handoverResolutions,
          resolvedIndexes: multiPatientContext.resolvedIndexes || [],
          selectedPatient: patient,
          currentQueue: reviewQueue,
          resolvingIndex: multiPatientContext.segmentIndex,
          language,
        });

        if (handoverOutcome.status === 'needs_selection') {
          setDraftGeneration(handoverOutcome.result);
          navigate(ROUTES.PATIENT_SELECT, {
            state: {
              generationResult: handoverOutcome.result,
              patientMatch: handoverOutcome.result.patientMatch,
              patientHints: handoverOutcome.result.patientHints,
              reviewQueue: handoverOutcome.queue,
              reviewQueueIndex: handoverOutcome.queue.length,
              multiPatientContext: handoverOutcome.multiPatientContext,
              recordingDurationSeconds,
            },
            replace: true,
          });
          return;
        }

        goToReview(handoverOutcome.queue);
        return;
      }

      if (multiPatientContext?.mode === 'segment' && multiPatientContext.segments) {
        const nextIndex = multiPatientContext.segmentIndex + 1;
        if (nextIndex < multiPatientContext.segments.length) {
          navigate(ROUTES.GENERATING, {
            replace: true,
            state: {
              transcript: multiPatientContext.segments[0]?.transcript || 'resume',
              encounterDateTime: multiPatientContext.encounterIso,
              recordingDurationSeconds,
              resumeMultiPatient: {
                segments: multiPatientContext.segments,
                startIndex: nextIndex,
                queue: nextQueue,
                encounterIso: multiPatientContext.encounterIso,
                encounterGroupId: multiPatientContext.encounterGroupId,
                cleanupFailed: multiPatientContext.cleanupFailed,
                sourceTranscriptRaw: multiPatientContext.sourceTranscriptRaw || null,
                sourceTranscriptCleaned: multiPatientContext.sourceTranscriptCleaned || null,
              },
            },
          });
          return;
        }
      }

      goToReview(nextQueue, reviewQueueIndex);
    } finally {
      setContinuing(false);
    }
  };

  if (!generationResult) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page px-4">
        <div className="text-center text-gray-500">{t('noNoteToReview')}</div>
      </div>
    );
  }

  const spokenName = patientHints?.name;
  const spokenRoom = patientHints?.room;
  const isMultiPatient = Boolean(multiPatientContext?.segmentTotal && multiPatientContext.segmentTotal > 1);
  const patientNumber = (multiPatientContext?.segmentIndex ?? 0) + 1;
  const segmentPreview = multiPatientContext?.segmentTranscript;

  return (
    <div className="min-h-screen bg-page pb-8">
      <header className="border-b border-gray-200 bg-surface px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.FREE_RECORD, { replace: true })}
          className="flex items-center gap-2 text-primary"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('back')}
        </button>
      </header>

      <div className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <div>
          {isMultiPatient ? (
            <>
              <p className="text-section-label text-primary">
                {t('selectPatientMultiBadge')
                  .replace('{current}', String(patientNumber))
                  .replace('{total}', String(multiPatientContext.segmentTotal))}
              </p>
              <h1 className="mt-2 text-[22px] font-bold text-gray-900">
                {t('selectPatientMultiTitle')}
              </h1>
              <p className="mt-2 text-sm text-gray-600">{t('selectPatientMultiSubtitle')}</p>
            </>
          ) : (
            <>
              <h1 className="text-[22px] font-bold text-gray-900">{t('selectPatientTitle')}</h1>
              <p className="mt-2 text-sm text-gray-600">{t('selectPatientSubtitle')}</p>
            </>
          )}

          {(spokenName || spokenRoom) && (
            <div className="mt-3 rounded-xl bg-primary-light px-3 py-2 text-sm text-primary">
              {spokenName && (
                <p>
                  {t('selectPatientHeard')}: <span className="font-semibold">{spokenName}</span>
                </p>
              )}
              {spokenRoom && (
                <p className={spokenName ? 'mt-1' : ''}>
                  {t('selectPatientRoomHeard')}: <span className="font-semibold">{spokenRoom}</span>
                </p>
              )}
            </div>
          )}

          {segmentPreview && (
            <div className="mt-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('selectPatientSegmentPreview')}
              </p>
              <p className="mt-1 leading-relaxed" dir="auto">
                {segmentPreview}
              </p>
            </div>
          )}
        </div>

        {matchedPatients.length > 0 && (
          <div className="space-y-2">
            <p className="text-section-label">{t('selectPatientAlreadyMatched')}</p>
            <div className="flex flex-wrap gap-2">
              {matchedPatients.map((entry) => (
                <MatchedPatientChip
                  key={entry.id}
                  name={entry.name}
                  room={entry.room}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {candidatePatients.map((patient) => (
            <PatientOption
              key={patient.id}
              patient={patient}
              subtitle={candidateSubtitle(patient.id)}
              onSelect={handleSelect}
              language={language}
              t={t}
              disabled={continuing}
            />
          ))}
        </div>

        {candidatePatients.length < allPatients.length && (
          <div className="space-y-2 border-t border-gray-200 pt-4">
            <p className="text-section-label">{t('selectPatientOther')}</p>
            {allPatients
              .filter((patient) => !candidatePatients.some((candidate) => candidate.id === patient.id))
              .map((patient) => (
                <PatientOption
                  key={patient.id}
                  patient={patient}
                  onSelect={handleSelect}
                  language={language}
                  t={t}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
